
/**
 * Module dependencies.
 */

import { MediaProps } from 'src/types/collection';
import {
  ProductColor,
  ProductFinish,
  ProductFormat,
  ProductFunction
} from 'src/types/products';

import { color, media, units } from 'src/styles/utils';
import {
  functions as configuredFunctions
} from 'src/core/collections-config/functions';

import {
  finishesIconsLarge,
  finishesValues
} from 'src/core/collections-config/finishes';

import { getKeyFromObject } from 'src/core/utils/objects';
import { ifProp } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import Box from 'src/components/core/layout/box';
import CardTitle from 'src/components/core/typography/card-title';
import Container from 'src/components/core/layout/container';
import Image from 'src/components/core/image';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import camelCase from 'lodash/camelCase';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  colorImages: MediaProps[],
  colors: ProductColor[],
  finishes: ProductFinish[],
  formats: ProductFormat[],
  functionImages: MediaProps[],
  functions: ProductFunction[],
  hasNoPaddingTop?: boolean,
  isKiosk?: boolean
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ hasNoPaddingTop?: boolean }>`
  background-color: ${color('beige100')};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0;
  `}

  ${ifProp('hasNoPaddingTop', css`
    padding-top: 0 !important;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas: '. content .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  ${media.min('ms')`
    grid-area: content;
  `}
`;

/**
 * `GridItems` styled component.
 */

const GridItems = styled.div`
  display: flex;
  flex-flow: row wrap;
`;

/**
 * `FlexWrapper` styled component.
 */

const FlexWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

/**
 * `RoundedItemShape` styled component.
 */

const RoundedItemShape = styled.span`
  border-radius: 100%;
  height: 119px;
  margin-bottom: ${units(2)};
  overflow: hidden;
  position: relative;
  width: 119px;

  ${media.min('ms')`
    height: 158px;
    width: 158px;
  `}
`;

/**
 * `FormatImageWrapper` styled component.
 */

const FormatImageWrapper = styled.div`
  color: ${color('grey200')};
  height: ${units(39)};
  margin-bottom: ${units(2)};
  position: relative;
  width: ${units(19.5)};

  ${media.min('sm')`
    height: ${units(40)};
    width: ${units(20)};
  `}

  ${media.min('lg')`
    height: ${units(45)};
    width: ${units(22.5)};
  `}
`;

/**
 * `FinishShape` styled component.
 */

const FinishShape = styled.span`
  align-items: center;
  background-color: ${color('white')};
  display: flex;
  height: 119px;
  justify-content: center;
  width: 119px;

  ${media.min('ms')`
    height: 158px;
    width: 158px;
  `}
`;

/**
 * `StyledSvg` styled component.
 */

const StyledSvg = styled(Svg)`
  ${media.max('ms')`
    max-width: ${units(9)}
  `}
`;

/**
 * `FormatValue` styled component.
 */

const FormatValue = styled(Type.Paragraph)`
  height: ${units(6)};
  padding: 0 ${units(1)};
  text-align: left;
  width: 100%;

  span {
    display: block;
  }
`;

/**
 * `FormatItem` component.
 */

const FormatItem = (props): ReactElement => {
  const { approximated, customProperties, imageUrl, name } = props;

  return (
    <FlexWrapper
      marginBottom={units(1)}
      marginBottomMs={units(2)}
      marginRight={units(1)}
      marginRightMs={units(2)}
    >
      <FormatImageWrapper>
        {imageUrl && (
          <Image
            alt={customProperties?.title ? customProperties?.title : name}
            layout={'fill'}
            objectFit={'cover'}
            src={imageUrl}
          />
        )}
      </FormatImageWrapper>

      <FormatValue>
        {name}

        {approximated && (
          <span>
            {`[${approximated.split('.').join(',')} cm]`}
          </span>
        )}
      </FormatValue>

    </FlexWrapper>
  );
};

/**
 * `RoundedItem` component.
 */

const RoundedItem = ({ fileUrl, name }): ReactElement => (
  <FlexWrapper
    marginBottom={units(6)}
    marginBottomMs={units(10)}
    marginRight={units(1)}
    marginRightMs={units(6)}
    textAlign={'center'}
    width={'119px'}
    widthMs={'158px'}
  >
    <RoundedItemShape>
      {fileUrl && (
        <Image
          alt={name}
          layout={'fill'}
          objectFit={'cover'}
          src={fileUrl}
        />
      )}
    </RoundedItemShape>

    <CardTitle size={'small'}>
      {name}
    </CardTitle>
  </FlexWrapper>
);

/**
 * Normalize functions.
 */

function normalizeFunctions(functions: ProductFunction[], functionImages: MediaProps[]) {
  const validFunctions = filter(functions, (functionName: string) => {
    return includes(Object.values(configuredFunctions), functionName);
  });

  const validFunctionImages = filter(functionImages, ({ customProperties }) => {
    return includes(validFunctions, customProperties?.function);
  });

  return map(validFunctionImages, ({ customProperties, fileUrl, id }) => ({
    ...customProperties,
    fileUrl,
    functionKey: getKeyFromObject(configuredFunctions, customProperties?.function),
    id
  }));
}

/**
 * Normalize finishes.
 */

function normalizeFinishes(finishes: ProductFinish[]) {
  const validFinishes = Object.values(finishesValues);

  return filter(validFinishes, finish => includes(finishes, finish));
}

/**
 * `SpecsSection` component.
 */

const SpecsSection = (props: Props): ReactElement => {
  const {
    colors,
    finishes,
    formats,
    functionImages,
    functions,
    hasNoPaddingTop,
    isKiosk
  } = props;

  const { translate } = useTranslate();
  const normalizedFunctions = normalizeFunctions(functions, functionImages);
  const normalizedFinishes = normalizeFinishes(finishes);
  const normalizedFormats = sortBy(formats, ['sortOrder']);

  return (
    <Section hasNoPaddingTop={hasNoPaddingTop}>
      <Container hasPadding={!isKiosk}>
        <Grid>
          <Content>
            {!isEmpty(colors) && (
              <Box
                marginBottom={units(5)}
                marginBottomMs={units(4)}
              >
                <Type.H2
                  marginBottom={units(4)}
                  marginBottomMs={units(6)}
                >
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    {translate('common:labels.color', {
                      count: size(colors)
                    })}
                  </ParallaxFadeInUp>
                </Type.H2>

                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  <GridItems>
                    {map(colors, ({ colorName, fileUrl, id }) => (
                      <RoundedItem
                        fileUrl={fileUrl}
                        key={id}
                        name={colorName}
                      />
                    ))}
                  </GridItems>
                </ParallaxFadeInUp>
              </Box>
            )}

            {!isEmpty(normalizedFunctions) && (
              <Box
                marginBottom={units(5)}
                marginBottomMs={units(4)}
              >
                <Type.H2
                  marginBottom={units(4)}
                  marginBottomMs={units(6)}
                >
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    {translate('common:labels.function', {
                      count: size(normalizedFunctions)
                    })}
                  </ParallaxFadeInUp>
                </Type.H2>

                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  <GridItems>
                    {map(normalizedFunctions, ({ fileUrl, functionKey, id }) => (
                      <RoundedItem
                        fileUrl={fileUrl}
                        key={id}
                        name={translate(`products:functions.${functionKey}`)}
                      />
                    ))}
                  </GridItems>
                </ParallaxFadeInUp>
              </Box>
            )}

            {!isEmpty(normalizedFormats) && (
              <Box
                marginBottom={units(7.5)}
                marginBottomMs={units(10.5)}
              >
                <Type.H2>
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    {translate('common:labels.format', {
                      count: size(normalizedFormats)
                    })}
                  </ParallaxFadeInUp>
                </Type.H2>

                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  <GridItems>
                    {map(normalizedFormats, format => (
                      <FormatItem
                        key={format.id}
                        {...format}
                      />
                    ))}
                  </GridItems>
                </ParallaxFadeInUp>
              </Box>
            )}

            {!isEmpty(normalizedFinishes) && (
              <>
                <Type.H2
                  marginBottom={units(4)}
                  marginBottomMs={units(6)}
                >
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    {translate('common:labels.finish', {
                      count: size(normalizedFinishes)
                    })}
                  </ParallaxFadeInUp>
                </Type.H2>

                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  <GridItems>
                    {map(normalizedFinishes, (finish: string) => (
                      <FinishShape key={finish}>
                        <StyledSvg
                          icon={finishesIconsLarge[camelCase(finish)]}
                          size={units(15)}
                        />
                      </FinishShape>
                    ))}
                  </GridItems>
                </ParallaxFadeInUp>
              </>
            )}
          </Content>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `SpecsSection` component.
 */

export default SpecsSection;
